import { createEffect, createSignal, type JSX, on, onCleanup, Show } from 'solid-js';
import { t } from '../../i18n';
import { createDragEngine, type GhostView } from '../../lib/drag-engine';
import type { MoveSubject } from '../../lib/move-items';
import { useMovePage } from '../../lib/move-page';
import { decimal } from '../../lib/time';
import { createToast, NOTICE_TTL_MS } from '../../lib/toast';

// The Fleet page's drag layer (#6771, epic #6552; prototype ghostHTML `:1933-1938`, the toast
// `:3288`, the `.dz-*` rules `:915-976`): ONE per page, mounted by `FleetMachines` under the
// page's move controller (`lib/move-page.ts`). It renders the ghost, the 2.6 s toast near a
// refused row and the page's move announcements; the pointer, Esc and the frame loop are the
// drag engine's (`lib/drag-engine.ts`), armed by a row's press (`pressMove`, `lib/card-move.ts`).
//
// The ghost's face: an accent dot, "<Role>", "Epic <title>" or "<Role> group", a badge ("N
// jobs" for an epic, "Epic <title>" for a job in one), the title, and "CPU N% · X.X GB memory"
// from the jobs' real usage, omitted when it is unknown. An epic or a group of more than one
// job is a stacked ghost.

/** The ghost's meta line from the jobs' real usage; null when either reading is unknown. */
function ghostMeta(subject: MoveSubject): string | null {
  let cpu: number | null = null;
  let mem: number | null = null;
  for (const j of subject.jobs) {
    if (j.usage?.cpu_pct != null) cpu = (cpu ?? 0) + j.usage.cpu_pct;
    if (j.usage?.mem_bytes != null) mem = (mem ?? 0) + j.usage.mem_bytes;
  }
  if (cpu === null || mem === null) return null;
  return t('fleet.move.ghost_meta', { cpu: Math.round(cpu), memory: decimal(mem / 1e9, 1) });
}

export function DragLayer(): JSX.Element {
  const page = useMovePage();
  const toast = createToast(NOTICE_TTL_MS);
  const [toastAt, setToastAt] = createSignal({ left: 0, top: 0 });
  const [ghost, setGhost] = createSignal<GhostView | null>(null);
  const [tone, setTone] = createSignal<'ok' | 'no' | null>(null);
  let ghostEl: HTMLDivElement | undefined;
  if (page !== null) {
    const engine = createDragEngine(page, {
      ghostEl: () => ghostEl,
      showGhost: setGhost,
      setTone,
      toast: (text, at) => {
        setToastAt(at);
        toast.notify(text, 'error');
      },
    });
    createEffect(
      on(page.pressed, (p) => {
        if (p !== null) engine.arm(p);
      }),
    );
    document.addEventListener('click', engine.swallow, true);
    onCleanup(() => {
      document.removeEventListener('click', engine.swallow, true);
      engine.dispose();
    });
  }
  return (
    <Show when={page}>
      {(p) => (
        <>
          <p class="sr-only" role="status">
            {p().live()}
          </p>
          <Show when={ghost()}>
            {(g) => (
              <div
                ref={ghostEl}
                class="move-ghost"
                classList={{ 'move-ghost--stack': g().stacked }}
                style={{ width: `${g().width}px` }}
                aria-hidden="true"
              >
                <div class="move-ghost__layer move-ghost__layer--back" />
                <div class="move-ghost__layer move-ghost__layer--mid" />
                <div
                  class="move-ghost__face"
                  classList={{
                    'move-ghost__face--live': tone() === 'ok',
                    'move-ghost__face--no': tone() === 'no',
                  }}
                >
                  <div class="move-ghost__top">
                    <span class="move-ghost__dot" />
                    <span>{g().subject.top}</span>
                    <Show when={g().subject.badge}>
                      {(b) => <span class="move-ghost__badge">{b()}</span>}
                    </Show>
                  </div>
                  <div class="move-ghost__title">{g().subject.title}</div>
                  <Show when={ghostMeta(g().subject)}>
                    {(meta) => <div class="move-ghost__meta">{meta()}</div>}
                  </Show>
                </div>
              </div>
            )}
          </Show>
          <Show when={toast.toast()}>
            {(n) => (
              <div
                class="move-toast"
                style={{ left: `${toastAt().left}px`, top: `${toastAt().top}px` }}
                aria-hidden="true"
              >
                {n().message}
              </div>
            )}
          </Show>
        </>
      )}
    </Show>
  );
}
